import type { ClassItem, ClassSession } from "@/data/classes";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;
export type DayName = (typeof DAY_NAMES)[number];

/** Extract every weekday mentioned in a free-form schedule/dates string. */
export function parseWeekdays(text: string | undefined | null): DayName[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  const found: DayName[] = [];
  for (const day of DAY_NAMES) {
    if (lower.includes(day.toLowerCase())) found.push(day);
  }
  return found;
}

export interface ParsedTime {
  /** Display like "9:20 AM – 10:20 AM" or "11:00 AM – 12:00 PM". */
  display: string;
  /** Start time in minutes past midnight (for sorting). */
  minutes: number;
}

/** Parse a time-of-day range out of a free-form string. Handles bare start times too. */
export function parseTime(text: string | undefined | null): ParsedTime | null {
  if (!text) return null;

  // Full range with explicit start meridiem: "9:20 AM – 10:20 AM"
  const full = text.match(
    /(\d{1,2}):(\d{2})\s*(AM|PM)\s*[–\-—]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i,
  );
  if (full) {
    const startMin = toMinutes(full[1], full[2], full[3]);
    return {
      display: `${normalize(full[1], full[2], full[3])} – ${normalize(full[4], full[5], full[6])}`,
      minutes: startMin,
    };
  }

  // Range with shared meridiem: "9:20 – 10:20 AM" or "11:00–12:00 PM"
  const shared = text.match(
    /(\d{1,2}):(\d{2})\s*[–\-—]\s*(\d{1,2}):(\d{2})\s*(AM|PM)/i,
  );
  if (shared) {
    const endPeriod = shared[5].toUpperCase();
    const endHour = parseInt(shared[3]);
    let startHour = parseInt(shared[1]);
    let startPeriod = endPeriod;
    if (endPeriod === "PM" && startHour > endHour && startHour !== 12) startPeriod = "AM";
    if (endPeriod === "AM" && startHour === 12) startPeriod = "AM";
    const startMin = toMinutes(shared[1], shared[2], startPeriod);
    return {
      display: `${normalize(shared[1], shared[2], startPeriod)} – ${normalize(shared[3], shared[4], endPeriod)}`,
      minutes: startMin,
    };
  }

  // Single time: "10:00 AM"
  const single = text.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (single) {
    return {
      display: normalize(single[1], single[2], single[3]),
      minutes: toMinutes(single[1], single[2], single[3]),
    };
  }
  return null;
}

function toMinutes(h: string, m: string, period: string): number {
  let hours = parseInt(h);
  const minutes = parseInt(m);
  const p = period.toUpperCase();
  if (p === "PM" && hours !== 12) hours += 12;
  if (p === "AM" && hours === 12) hours = 0;
  return hours * 60 + minutes;
}

function normalize(h: string, m: string, period: string): string {
  return `${parseInt(h)}:${m} ${period.toUpperCase()}`;
}

export interface SessionSlot {
  /** Weekday this slot occurs on. */
  weekday: DayName;
  /** Display "9:20 AM – 10:20 AM" (may be "" if unparseable). */
  timeDisplay: string;
  /** Sort key. */
  timeMinutes: number;
  /** Session labels sharing this weekday+time slot for the class. */
  sessionLabels: string[];
}

/**
 * Derive every recurring (weekday, time) slot a class meets on, from its sessions.
 * Each session's own `dates` string is inspected first for weekday and time; if a
 * field is missing there we fall back to the class-level `schedule` string so
 * legacy entries still render.
 */
export function getClassSessionSlots(c: ClassItem): SessionSlot[] {
  const fallbackDays = parseWeekdays(c.schedule);
  const fallbackTime = parseTime(c.schedule);
  const sessions: ClassSession[] = c.sessions?.length ? c.sessions : [];

  // No sessions at all → fall back to whatever the class-level schedule says.
  if (sessions.length === 0) {
    if (fallbackDays.length === 0) return [];
    return fallbackDays.map((weekday) => ({
      weekday,
      timeDisplay: fallbackTime?.display ?? "",
      timeMinutes: fallbackTime?.minutes ?? 9999,
      sessionLabels: [],
    }));
  }

  const bucket = new Map<string, SessionSlot>();
  for (const s of sessions) {
    const days = parseWeekdays(s.dates);
    const effectiveDays = days.length ? days : fallbackDays;
    const time = parseTime(s.dates) ?? fallbackTime;
    if (effectiveDays.length === 0) continue;
    for (const weekday of effectiveDays) {
      const key = `${weekday}|${time?.display ?? ""}`;
      const existing = bucket.get(key);
      if (existing) {
        if (!existing.sessionLabels.includes(s.label)) existing.sessionLabels.push(s.label);
      } else {
        bucket.set(key, {
          weekday,
          timeDisplay: time?.display ?? "",
          timeMinutes: time?.minutes ?? 9999,
          sessionLabels: [s.label],
        });
      }
    }
  }
  return Array.from(bucket.values());
}
